import * as d3 from "d3";
import { Position } from "plano";
import { D3Utils } from "../../../utils/D3Utils";
import { MathUtils } from "../../../utils/MathUtils";
import Globals from "../../globals";
import { store } from "./../../../store/store";
import { Plano } from "./Plano";

/**
 * Recursively check if it is possible to drop a Component
 * at the given position
 *
 * If options.snap is true, the computation is based on the potential destination
 *
 * @param {{ x: number, y: number }} position
 * @param {Component} component
 * @param {{ snap: boolean }} options
 * @return {boolean}
 */
function canDropTo(position, component, { snap } = { snap: false }) {
    if (snap) {
        position = component.getPositionSnap(position);
    }

    if (!component.canSnap(position)) {
        return false;
    }
    for (const child of component.children) {
        if (!canDropTo(child.getPositionFromParent(position), child)) {
            return false;
        }
    }
    return true;
}

export class Component {
    /** @type {Component[]} */
    static allComponents = [];

    static TYPE_MODULE = "MODULE";
    static TYPE_SHELF = "SHELF";
    static TYPE_GROUP = "GROUP";
    static TYPE_DIVIDER = "DIVIDER";
    static mousePositionStart = { x: 0, y: 0 };
    static mousePositionEnd = { x: 0, y: 0 };

    data = null;
    /** @type {Element} */
    elt = null;
    eltD3 = null;
    /** @type {Component | undefined} */
    parent = null;
    type = null;
    components = [];
    positionOrigin = { x: 0, y: 0 };
    positionStart = { x: 0, y: 0 };
    positionEnd = { x: 0, y: 0 };
    distance = { x: 0, y: 0 };
    positionMouseStart = { x: 0, y: 0 };
    support = null;
    isNew = false;
    _selected = false;
    isDrag = false;
    isDuplicate = false;

    constructor(componentData, parent, editor = false) {
        this.data = Component.parseNumber(componentData, ["width", "height", "x", "y", "xParent", "xOrigin"]);

        if (parent) {
            this.parent = parent;
            parent.addComponent(this);
        }
        // This has nothing to do here
        if (!editor) Component.allComponents.push(this);
    }

    static parseNumber(data, keys) {
        let value;
        Object.keys(data).forEach(key => {
            value = data[key];
            if (keys.includes(key) && !isNaN(value)) {
                data[key] = MathUtils.convertFloat(value);
            }
        });

        return data;
    }

    // Why?
    static getAllComponents() {
        return Component.allComponents;
    }

    static getAllGroups() {
        return Component.allComponents.filter(cmp => cmp.type === Component.TYPE_GROUP);
    }

    static getAllDividers() {
        return Component.allComponents.filter(cmp => cmp.type === Component.TYPE_DIVIDER);
    }

    static getAllModules() {
        return Component.allComponents.filter(cmp => cmp.type === Component.TYPE_MODULE);
    }

    static getComponent(UUID) {
        let component = Component.allComponents.find(comp => comp.data.UUID === UUID);
        return component;
    }

    static getSelectedComponents() {
        let components = Component.allComponents.filter(comp => comp._selected === true);
        return components;
    }

    static cleanComponents() {
        Component.allComponents = [];
    }

    static getJsonGroups(cmps = null) {
        cmps = cmps === null ? Component.getAllGroups() : cmps;
        let jsons = [];
        cmps.forEach(cmp => {
            jsons.push({
                data: cmp.data,
                components: Component.getJsonGroups(cmp.getComponents()),
                type: cmp.type
            });
        });
        return jsons;
    }

    static getJsonCaddySystem() {
        const rootComponents = new Set(
            (function*() {
                for (const component of Component.allComponents) {
                    var parent = component.parent;
                    while (parent && parent.parent) {
                        parent = parent.parent;
                    }
                    if (parent) {
                        yield parent;
                    }
                }
            })()
        );
        function _save(cmps) {
            let jsons = [];
            cmps.forEach(cmp => {
                jsons.push({
                    data: cmp.data,
                    components: _save(cmp.getComponents()),
                    type: cmp.type
                });
            });
            return jsons;
        }
        return _save(rootComponents);
    }

    static createSvgFavorite(cmp) {
        let xmlns = "http://www.w3.org/2000/svg";
        let boxWidth = 2000;
        let boxHeight = 2000;

        let svgElem = document.createElementNS(xmlns, "svg");
        svgElem.setAttributeNS(null, "width", boxWidth);
        svgElem.setAttributeNS(null, "height", boxHeight);

        svgElem.style.width = boxWidth + "px";
        svgElem.style.height = boxHeight + "px";

        let cmps = cmp.getAllComponentsChildren();
        cmps.push(cmp);
        cmps.sort((cmp1, cmp2) => cmp1.data.x - cmp2.data.x);
        let cmpStart = cmps[0];
        cmps.sort((cmp1, cmp2) => cmp2.data.x + cmp2.data.width - (cmp1.data.x + cmp1.data.width));
        let cmpEnd = cmps[0];
        let xStart = cmpStart.data.x;
        let xEnd = cmpEnd.data.x + cmpEnd.data.width;

        cmps.sort((cmp1, cmp2) => cmp1.data.y - cmp2.data.y);
        cmpStart = cmps[0];
        cmpEnd = cmps[cmps.length - 1];
        let yStart = cmpStart.data.y;
        let yEnd = cmpEnd.data.y + cmpEnd.data.height;

        let widthViewBox = xEnd - xStart;
        let heightViewBox = yEnd - yStart;

        let g = d3
            .select(svgElem)
            .attr("class", "fav-modules size1of2")
            .attr("viewBox", "0 0 " + widthViewBox + " " + heightViewBox)
            .append("g")
            .node();

        cmps.forEach(comp => {
            let elt = comp.getElement().cloneNode(true);
            d3.select(elt)
                .select("rect#component-selected")
                .remove();
            let { data } = comp;
            let x = data.x - xStart;
            let y = data.y - yStart;
            g.append(elt);
            d3.select(elt)
                .attr("x", x)
                .attr("y", y);
        });

        return { elt: svgElem, size: { width: widthViewBox, height: heightViewBox }, hookInverse: !!cmp.data.hookInverse };
    }

    static getJsonDividers() {
        let jsons = [];
        let dividers = Component.getAllDividers();

        dividers.forEach(divider => {
            jsons.push({
                data: divider.data,
                type: divider.type
            });
        });

        return jsons;
    }

    static hasOverHang() {
        return store.getters["plano/status/hasOverHang"];
    }

    static hasSnapMod() {
        return store.getters["plano/status/hasSnapMod"];
    }

    static conditionDimension(dim1, dim2) {
        let conditionX = (dim1.x < dim2.xEnd || dim1.x < dim2.x) && (dim1.xEnd > dim2.x || dim1.xEnd > dim2.xEnd);
        let conditionY = (dim1.y < dim2.yEnd || dim1.y < dim2.y) && (dim1.yEnd > dim2.y || dim1.yEnd > dim2.yEnd);
        return conditionX && conditionY;
    }

    /** Is outside of the "Inner" */
    static conditionOutInner(dim) {
        let inner = store.getters["planonext/carcass/getInner"];
        let { x: xInner, y: yInner, width: wInner, height: hInner } = inner;
        let { x, y, xEnd, yEnd } = dim;

        let xEndInner = xInner + wInner;
        let yEndInner = yInner + hInner;

        let conditionX = xInner <= x && xEndInner >= xEnd;
        let conditionY = yInner <= y && yEndInner >= yEnd;
        return conditionY && conditionX;
    }

    static deselectedComponents() {
        Component.getSelectedComponents().forEach(cmp => {
            cmp.deselectedComponent();
        });
    }

    // Some cooler name for "components"
    /**
     * @type {Component[]}
     */
    get children() {
        return this.components;
    }

    set children(value) {
        this.components = value;
    }

    get nonGroupChildren() {
        return this.children.reduce((children, current) => {
            if (current.type === Component.TYPE_GROUP) {
                children.push(...current.nonGroupChildren);
            } else {
                children.push(current);
            }
            return children;
        }, []);
    }

    deleteComponent(comp) {
        this.components.splice(this.components.indexOf(comp), 1);
    }

    getJson() {
        return {
            data: this.data,
            type: this.type,
            components: Component.getJsonGroups(this.getComponents())
        };
    }

    /**
     * @return Tous components (sauf Group) qui n'appartient pas à son group
     */
    getComponentsOutOfGroup() {
        let group = this.getGroup();
        let components = Component.allComponents.filter(cmp => cmp.type !== Component.TYPE_GROUP && cmp.getGroup() !== group);
        return components;
    }

    getAllComponentsChildren(cmps = []) {
        let components = this.getComponents();
        components.forEach(cmp => {
            cmps.push(cmp);
            cmps.concat(cmp.getAllComponentsChildren(cmps));
        });

        return cmps;
    }

    addComponent(component) {
        this.components.push(component);
    }

    // I'll repeat myself but... Why?
    getComponents() {
        return this.components;
    }

    getGroup() {
        let parent = this.parent;
        if (parent === null) return this;

        while ((parent.type === Component.TYPE_GROUP) === false) {
            parent = parent.getGroup();
        }

        return parent;
    }

    getSuperParentByType(type = Component.TYPE_SHELF) {
        if (this.type === type) return this;
        let parent = this.getParent();
        if (parent.type !== type) {
            parent = parent.getSuperParentByType(type);
        }

        return parent;
    }

    /** @deprecated use `this.parent` instead  */
    getParent() {
        return this.parent;
    }

    // Why not just use this.parent?
    /** @deprecated */
    setParent(parent) {
        this.parent = parent;
    }

    changeParent(parent) {
        if (parent !== this.parent) {
            let group = parent.getGroup();
            let groupCurrent = this.parent.getGroup();
            if (group !== groupCurrent) {
                group.getElement().appendChild(this.getElement());
                this.getAllComponentsChildren().forEach(cmp => group.getElement().appendChild(cmp.getElement()));
            }
            let { components } = this.parent;
            components.splice(components.indexOf(this), 1);
            parent.addComponent(this);
            this.setParent(parent);
        }
    }

    isSelected() {
        return this._selected;
    }

    setSelected(value) {
        this._selected = value;
    }
    selectedComponent(multiple = false) {
        if (!multiple) Component.getSelectedComponents().forEach(cmp => cmp.deselectedComponent());
        this.setSelected(true);
        let elt = this.getElement();
        let bb = elt.getBBox();

        let rectSelected = d3.select(elt).select("rect#component-selected");
        if (rectSelected.size() === 0) {
            rectSelected = d3
                .select(elt)
                .append("rect")
                .attr("id", "component-selected")
                .attr("stroke", "#41B4E8")
                .attr("stroke-width", 10)
                .attr("fill-opacity", 0.1)
                .attr("fill", "#41B4E8")
                .attr("width", bb.width)
                .attr("height", bb.height);
        }

        rectSelected.attr("visibility", "visible");
    }

    deselectedComponent() {
        this.setSelected(false);
        let elt = this.getElement();
        let rectSelected = d3.select(elt).select("rect#component-selected");
        rectSelected.attr("visibility", "hidden");
    }

    static setMousePositionStart(event) {
        Component.mousePositionStart = { x: event.clientX, y: event.clientY };
    }

    static setMousePositionEnd(event) {
        let posStart = Component.mousePositionStart;
        Component.mousePositionEnd = { x: event.clientX - posStart.x, y: event.clientY - posStart.y };
    }

    startDrag = start => (d, i, cmps) => {
        this.selectedComponent();
        this.positionStart = { x: this.positionOrigin.x, y: this.positionOrigin.y };
        let pos = Plano.getMousePos(d3.event.sourceEvent);
        Component.setMousePositionStart(pos);

        let x = Math.abs(this.positionOrigin.x - pos.x);
        let y = Math.abs(this.positionOrigin.y - pos.y);
        this.positionMouseStart = { x, y };
        this.isDrag = true;
        return start ? start(d, i, cmps) : null;
    };

    endDrag = end => (d, i, cmps) => {
        if (end) {
            return ((d, i, cmps) => {
                let pos = Plano.getMousePos(d3.event.sourceEvent);
                Component.setMousePositionEnd(pos);
                let f = end(d, i, cmps);
                this.isDrag = false;
                this.positionEnd = { x: this.data.x, y: this.data.y };
                this.distance = {
                    x: this.positionEnd.x - this.positionStart.x,
                    y: this.positionEnd.y - this.positionStart.y
                };
                return f;
            })(d, i, cmps);
        }

        return end;
    };

    isParentDrag() {
        let parent = this.getParent();
        if (!parent) return false;

        if (parent.isDrag) return true;
        else if (parent !== null) return parent.isParentDrag();
        else return false;
    }

    drag(start, drag, end) {
        let startDrag = this.startDrag(start);
        let endDrag = this.endDrag(end);
        D3Utils.drag(this.eltD3, startDrag, drag, endDrag);
    }

    _remove() {
        let components = this.getComponents().map(cmp => cmp);
        components.forEach(component => {
            component._remove();
        });

        this.getElementD3().remove();
        this.components = [];
        let parent = this.getParent();
        if (parent !== null) {
            parent.deleteComponent(this);
        }
        Component.allComponents.splice(Component.allComponents.indexOf(this), 1);
    }

    delete() {
        this._remove();
    }

    duplicate() {}

    // Should be synchronous
    // and extract the SVG loading somewhere else
    async _init(data) {
        const elt = await this._loadSvg(data.src);
        if (elt) {
            this.elt = elt;
            this.getGroup().elt.appendChild(elt);
            this.eltD3 = d3
                .select(elt)
                .data([data])
                .attr("id", data.UUID)
                .attr("height", data.height)
                .attr("width", data.width);
            this.positionOrigin.x = data.x;
            this.positionOrigin.y = data.y;
            this.toPosition(this.positionOrigin);
        }
        return this;
    }

    getElement() {
        return this.elt;
    }

    getElementD3() {
        return this.eltD3;
    }

    /** @deprecated */
    getData() {
        return this.data;
    }

    /** @deprecated */
    setData(data) {
        this.data = data;
    }

    canSnap(pos) {
        let dim = this.getDimension(pos);
        if (!Component.conditionOutInner(dim)) return false;
        return true;
    }

    validate(pos) {
        return canDropTo(pos, this, { snap: Component.hasSnapMod() });
    }

    /** @deprecated use "position" instead */
    getDimension(pos = null) {
        // why data and not data.config?
        // It seems that  all components have width/height/x/y in data,
        // but config is specific to the component type
        let { width, height, x, y } = this.data;
        // why parse?
        x = MathUtils.convertFloat(pos ? pos.x : x, 1);
        y = MathUtils.convertFloat(pos ? pos.y : y, 1);
        width = MathUtils.convertFloat(width, 1);
        height = MathUtils.convertFloat(height, 1);
        let xEnd = x + width;
        let yEnd = y + height;
        let dimension = { x, y, xEnd, yEnd };
        return dimension;
    }

    /** @type {Position} */
    get position() {
        return Position.fromSize(this.data);
    }

    getPositionWithControle(pos) {
        if (!this.canSnap(pos)) {
            let { x, y } = this.positionOrigin;
            pos = { x, y };
        } else {
            this.positionOrigin = pos;
        }
        return pos;
    }

    getPositionSnap(pos) {
        return pos;
    }

    getPosition(pos) {
        this.positionOrigin = pos;
        return pos;
    }
    // with controle check if it's outside of the bounds or not
    toPosition(pos, withControle = true) {
        let data = this.data;
        if (Component.hasSnapMod() && !this.isDuplicate && withControle) pos = this.getPositionSnap(pos);

        let position = withControle ? this.getPositionWithControle(pos) : this.getPosition(pos);
        data.x = position.x;
        data.y = position.y;
        if (this.parent) {
            data.xParent = position.x - this.parent.data.x;
        }
        data.xOrigin = 0;
        d3.select(this.elt)
            .attr("x", d => {
                d.x = position.x;
                return d.x;
            })
            .attr("y", d => {
                d.y = position.y;
                return d.y;
            });

        for (const child of this.nonGroupChildren) {
            const posComponent = child.getPositionFromParent(position, data);
            child.toPosition(posComponent, withControle);
        }

        return position;
    }

    setPosition(pos) {
        d3.select(this.elt)
            .attr("x", d => {
                d.x = pos.x;
                return d.x;
            })
            .attr("y", d => {
                d.y = pos.y;
                return d.y;
            });
    }

    toPositionOrigin() {
        this.toPosition(this.positionOrigin);
    }

    findParentWhere(matcher) {
        var parent = this.parent;
        while (!!parent) {
            if (matcher(parent)) {
                return parent;
            }
            parent = parent.parent;
        }
        return undefined;
    }

    visitChildren(/** @type {(cmp: Component) => boolean} */ matcher) {
        // a non-recursive implementation of the visitor
        const queue = [];

        queue.push(...this.children);

        while (queue.length > 0) {
            const current = queue[0];
            queue.splice(0, 1);

            if (matcher(current)) {
                queue.splice(0, 0, ...current.children);
            }
        }
    }

    findAllDescendantsWhere(/** @type {(component: Component) => boolean} */ matcher) {
        const result = [];
        this.visitChildren(cmp => {
            if (matcher(cmp)) result.push(cmp);
            return true;
        });
        return result;
    }

    findFirstDescendantWhere(/** @type {(component: Component) => boolean} */ matcher) {
        var result;
        this.visitChildren(cmp => {
            if (matcher(cmp)) {
                result = cmp;
                return false;
            }
            return true;
        });
        return result;
    }

    findDescendantWithId(id) {
        this.findFirstDescendantWhere(cmp => cmp.data.UUID === id);
    }

    getPositionFromParent(positionParent, parentData = undefined) {
        // parentData allows to optionally override the computation
        parentData = parentData || this.parent.data;

        let xMod = positionParent.x + this.data.xParent;

        let { snapUp } = this.data;
        let relativeYPos = parentData.relativeYPos ? Component.parseNumber(parentData.relativeYPos) : 0;

        let yMod = snapUp ? positionParent.y + relativeYPos : positionParent.y - this.data.height + relativeYPos;
        if (this.parent.type === Component.TYPE_MODULE && snapUp) {
            yMod += parentData.height;
        }
        let posMod = { x: xMod, y: yMod };

        const caddy = this.findParentWhere(parent => parent.data.type === Component.TYPE_SHELF && parent.data.isCaddy);
        if (caddy && this.type === Component.TYPE_SHELF) {
            posMod.x = this.data.x;
            posMod.y = this.data.y;
        }
        return posMod;
    }

    _cleanUrl(urlToClean) {
        if (!urlToClean) return "";
        let url = "";
        if (urlToClean.startsWith("http")) {
            let urlReplace = urlToClean.replace("http://", "").replace("https://", "");
            url = Globals.prefixUrl + urlReplace;
        } else {
            url = Globals.getBaseUrl() + urlToClean;
        }
        return url;
    }

    _drawImage(svg) {
        let DOMURL = self.URL || self.webkitURL || self;
        let blob = new Blob([svg.outerHTML], { type: "image/svg+xml;charset=utf-8" });
        let url = DOMURL.createObjectURL(blob);
        let xmlns = "http://www.w3.org/2000/svg";
        let image = document.createElementNS(xmlns, "image");
        image.setAttributeNS(null, "href", url);
        return image;
    }

    _loadSvg(api) {
        let url = this._cleanUrl(api);
        let promise = new Promise((resolve, reject) => {
            d3.xml(url)
                .mimeType("image/svg+xml")
                .get((error, xml) => {
                    if (error) {
                        console.error(error, url, "error de le cache image");
                        reject(url);
                    } else {
                        let dom = xml.documentElement;
                        resolve(dom);
                    }
                });
        }).then(res => {
            return res;
        });
        return promise;
    }
}



// WEBPACK FOOTER //
// ./src/components/plano/components/Component.js